import React, { useState, useEffect, useCallback } from "react";
import clubService from "../../services/clubService";
import { useNotification } from "../../context/NotificationContext";
import AddClubMember from "./AddClubMember";

const ClubMembers = ({ club, onClose, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [showAddMember, setShowAddMember] = useState(false);
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const { showSuccess, showError } = useNotification();

  const fetchClubMembers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await clubService.getClubMemberships(
        club.id,
        page,
        10,
        searchTerm
      );

      if (response.success && response.data) {
        setMembers(response.data.data || []);
        setTotalPages(response.data.totalPages || 0);
        setTotalElements(response.data.totalElements || 0);
      } else {
        setMembers([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch (error) {
      console.error("Error fetching club members:", error);
      showError("Failed to fetch club members");
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [club.id, page, searchTerm, showError]);

  // Fetch club members on component mount and when dependencies change
  useEffect(() => {
    if (club?.id) {
      fetchClubMembers();
    }
  }, [club?.id, fetchClubMembers]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleRoleChange = async (member) => {
    if (!newRole) {
      showError("Please select a role");
      return;
    }

    try {
      setLoading(true);

      const response = await clubService.updateMemberRole(
        club.id,
        member.userId,
        {
          userId: member.userId,
          role: newRole,
        }
      );

      if (response.success) {
        showSuccess("Member role updated successfully!");
        setEditingMember(null);
        setNewRole("");
        await fetchClubMembers(); // Refresh the members list
        onRefresh();
      }
    } catch (error) {
      console.error("Error updating member role:", error);
      showError("Failed to update member role");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (member) => {
    if (
      window.confirm(
        `Are you sure you want to remove ${member.fullName} from the club?`
      )
    ) {
      try {
        setLoading(true);

        const response = await clubService.removeMember(club.id, member.userId);

        if (response.success) {
          showSuccess("Member removed successfully!");
          await fetchClubMembers(); // Refresh the members list
          onRefresh();
        }
      } catch (error) {
        console.error("Error removing member:", error);
        showError("Failed to remove member");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAddMemberSuccess = async () => {
    setShowAddMember(false);
    await fetchClubMembers(); // Refresh the members list
    onRefresh();
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      ADMIN: { bg: "danger", icon: "fa-crown", text: "Admin" },
      MODERATOR: { bg: "warning", icon: "fa-shield-alt", text: "Moderator" },
      MEMBER: { bg: "primary", icon: "fa-user", text: "Member" },
    };

    const config = roleConfig[role] || roleConfig["MEMBER"];

    return (
      <span
        className={`badge bg-${config.bg} bg-opacity-10 text-${config.bg} fw-bold`}
      >
        <i className={`fas ${config.icon} me-1`}></i>
        {config.text}
      </span>
    );
  };

  return (
    <div className="club-members">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          <i className="fas fa-users me-2"></i>
          Club Members ({totalElements})
        </h5>
        <div>
          <button
            className="btn btn-primary btn-sm me-2"
            onClick={() => setShowAddMember(true)}
          >
            <i className="fas fa-user-plus me-1"></i>
            Add Member
          </button>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={onClose}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      </div>

      {showAddMember ? (
        <AddClubMember
          club={club}
          onClose={() => setShowAddMember(false)}
          onRefresh={handleAddMemberSuccess}
        />
      ) : (
        <>
          {/* Search Input */}
          <div className="mb-4">
            <div className="input-group">
              <span className="input-group-text">
                <i className="fas fa-search"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search members by name, student number, or email..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : members && members.length > 0 ? (
            <>
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead className="bg-light">
                    <tr>
                      <th className="fw-bold">Member</th>
                      <th className="fw-bold">Student Number</th>
                      <th className="fw-bold">Role</th>
                      <th className="fw-bold text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => (
                      <tr key={member.userId}>
                        <td>
                          <div className="d-flex align-items-center">
                            <div
                              className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                              style={{ width: "40px", height: "40px" }}
                            >
                              <i className="fas fa-user text-primary"></i>
                            </div>
                            <div>
                              <div className="fw-bold">{member.fullName}</div>
                              <small className="text-muted">
                                {member.email}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="text-muted">
                            {member.studentNumber}
                          </span>
                        </td>
                        <td>
                          {editingMember === member.userId ? (
                            <div className="d-flex align-items-center gap-2">
                              <select
                                className="form-select form-select-sm"
                                value={newRole}
                                onChange={(e) => setNewRole(e.target.value)}
                                style={{ width: "120px" }}
                              >
                                <option value="">Select Role</option>
                                <option value="ADMIN">Admin</option>
                                <option value="MODERATOR">Moderator</option>
                                <option value="MEMBER">Member</option>
                              </select>
                              <button
                                className="btn btn-success btn-sm"
                                onClick={() => handleRoleChange(member)}
                                disabled={loading}
                              >
                                {loading ? (
                                  <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                  <i className="fas fa-check"></i>
                                )}
                              </button>
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => {
                                  setEditingMember(null);
                                  setNewRole("");
                                }}
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          ) : (
                            getRoleBadge(member.role)
                          )}
                        </td>
                        <td className="text-center">
                          {editingMember !== member.userId && (
                            <div className="btn-group btn-group-sm">
                              <button
                                className="btn btn-outline-primary"
                                onClick={() => {
                                  setEditingMember(member.userId);
                                  setNewRole(member.role);
                                }}
                                title="Change Role"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                className="btn btn-outline-danger"
                                onClick={() => handleRemoveMember(member)}
                                disabled={loading}
                                title="Remove Member"
                              >
                                {loading ? (
                                  <span className="spinner-border spinner-border-sm"></span>
                                ) : (
                                  <i className="fas fa-user-minus"></i>
                                )}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-4">
                  <div className="text-muted">
                    Showing {page * 10 + 1} to{" "}
                    {Math.min((page + 1) * 10, totalElements)} of{" "}
                    {totalElements} members
                  </div>
                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li
                        className={`page-item ${page === 0 ? "disabled" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setPage(page - 1)}
                          disabled={page === 0}
                        >
                          Previous
                        </button>
                      </li>
                      {[...Array(Math.min(5, totalPages))].map((_, index) => {
                        const pageNum = Math.max(
                          0,
                          Math.min(page - 2 + index, totalPages - 5 + index)
                        );
                        return (
                          <li
                            key={pageNum}
                            className={`page-item ${
                              page === pageNum ? "active" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => setPage(pageNum)}
                            >
                              {pageNum + 1}
                            </button>
                          </li>
                        );
                      })}
                      <li
                        className={`page-item ${
                          page === totalPages - 1 ? "disabled" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setPage(page + 1)}
                          disabled={page === totalPages - 1}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-5">
              <i
                className="fas fa-users text-muted mb-3"
                style={{ fontSize: "3rem" }}
              ></i>
              <h6 className="text-muted">No Members Found</h6>
              <p className="text-muted">
                {searchTerm
                  ? "No members match your search criteria."
                  : "This club doesn't have any members yet."}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ClubMembers;
